import React, { useEffect, useState } from "react";
import { fetchApi, sendApi } from "../../apis/Index";
import { toast } from "react-hot-toast";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingId, setTrackingId] = useState("");
  const [shippingService, setShippingService] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const result = await fetchApi("https://wittynailtip.com/backend/admin-orders.php");
      if (result.data.success) {
        setOrders(result.data.orders);
      } else {
        toast.error(result.data.message || "Failed to fetch orders");
      }
    } catch (error) {
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrder = async () => {
    if (!trackingId || !shippingService) {
      toast.error("Please provide a tracking ID and Shipping Service.");
      return;
    }

    setUpdating(true);
    const updateData = {
      cart_id: selectedOrder.cart_id,
      track_id: trackingId,
      shipping: shippingService,
      email: selectedOrder.customer_email
    };

    try {
      const result = await sendApi(updateData, "https://wittynailtip.com/backend/admin-update-order.php");
      if (result.data.success) {
        toast.success(result.data.message);
        setSelectedOrder(null);
        setTrackingId("");
        setShippingService("");
        fetchOrders(); // Refresh the list
      } else {
        toast.error(result.data.message || "Failed to update order");
      }
    } catch (error) {
      toast.error("An error occurred while updating.");
    } finally {
      setUpdating(false);
    }
  };

  const closeModal = () => {
    setSelectedOrder(null);
    setTrackingId("");
    setShippingService("");
  };

  const getFullAddress = (order) => {
    const parts = [
      order.customer_street,
      order.customer_city,
      order.customer_state,
      order.customer_zip
    ].filter(Boolean);
    
    if (parts.length > 0) {
      return parts.join(', ');
    }
    return order.customer_billing || "No address provided";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FFB7CF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manage Orders</h1>
          <p className="text-gray-500 mt-1">View and track all customer orders.</p>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50/50">
              <tr>
                <th scope="col" className="px-6 py-4 rounded-tl-3xl">Order Ref</th>
                <th scope="col" className="px-6 py-4">Customer</th>
                <th scope="col" className="px-6 py-4">Item</th>
                <th scope="col" className="px-6 py-4">Date</th>
                <th scope="col" className="px-6 py-4">Status</th>
                <th scope="col" className="px-6 py-4 rounded-tr-3xl">Amount</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No orders found.
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr 
                    key={order.id} 
                    onClick={() => setSelectedOrder(order)}
                    className="bg-white border-b border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                      {order.payment_ref}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{order.customer_name || "Guest"}</div>
                      <div className="text-xs text-gray-400">{order.customer_email || ""}</div>
                    </td>
                    <td className="px-6 py-4">
                      {order.product_name || order.product_id}
                      <span className="block text-xs text-gray-400">Qty: {order.quantity} | Size: {order.size}</span>
                    </td>
                    <td className="px-6 py-4">
                      {order.order_date}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full capitalize ${
                        order.tracking === 'paid' ? 'bg-[#E1F7EB] text-[#20AA5F]' : 
                        order.tracking === 'shipped' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {order.tracking}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-bold text-gray-900">
                      ${order.price}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden font-jost animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Order Details</h3>
                <p className="text-sm text-gray-500">Ref: {selectedOrder.payment_ref}</p>
              </div>
              <button 
                onClick={closeModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto">
              {/* Product Info */}
              <div className="bg-gray-50 p-4 rounded-2xl">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Product</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Item</p>
                    <p className="font-medium text-gray-900">{selectedOrder.product_name || selectedOrder.product_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Color</p>
                    <p className="font-medium text-gray-900">{selectedOrder.color}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Size</p>
                    <p className="font-medium text-gray-900">{selectedOrder.size}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Quantity</p>
                    <p className="font-medium text-gray-900">{selectedOrder.quantity}</p>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="bg-gray-50 p-4 rounded-2xl">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Customer</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Name</p>
                    <p className="font-medium text-gray-900">{selectedOrder.customer_name || 'Guest'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Phone</p>
                    <p className="font-medium text-gray-900">{selectedOrder.customer_phone || 'N/A'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">{selectedOrder.customer_email}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-gray-500">Shipping Address</p>
                    <p className="font-medium text-gray-900">{getFullAddress(selectedOrder)}</p>
                  </div>
                </div>
              </div>

              {/* Total & Action */}
              <div className="flex flex-col md:flex-row gap-6 justify-between items-start md:items-center pt-2">
                <div>
                  <p className="text-gray-500 text-sm">Total Amount</p>
                  <p className="text-3xl font-bold text-gray-900">${selectedOrder.price}</p>
                </div>
                
                {selectedOrder.tracking !== 'delivered' && selectedOrder.tracking !== 'shipped' ? (
                  <div className="w-full md:w-auto flex flex-col md:flex-row gap-3">
                    <input 
                      type="text" 
                      placeholder="Tracking ID" 
                      value={trackingId}
                      onChange={(e) => setTrackingId(e.target.value)}
                      className="px-4 py-2 border border-gray-200 rounded-xl outline-none focus:border-[#FFB7CF] focus:ring-2 focus:ring-[#FFB7CF]/20 transition-all text-sm"
                    />
                    <select 
                      value={shippingService}
                      onChange={(e) => setShippingService(e.target.value)}
                      className="px-4 py-2 border border-gray-200 rounded-xl outline-none focus:border-[#FFB7CF] focus:ring-2 focus:ring-[#FFB7CF]/20 transition-all text-sm bg-white"
                    >
                      <option value="" disabled>Service</option>
                      <option value="ups">UPS</option>
                      <option value="usps">USPS</option>
                    </select>
                    <button 
                      onClick={handleUpdateOrder}
                      disabled={updating}
                      className="bg-[#FFB7CF] text-white px-6 py-2 rounded-xl font-medium hover:bg-[#ff9ebc] transition-colors disabled:opacity-70 text-sm"
                    >
                      {updating ? 'Marking...' : 'MARK AS SENT'}
                    </button>
                  </div>
                ) : (
                  <div className="text-right">
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#E1F7EB] text-[#20AA5F] rounded-xl font-semibold text-sm">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      {selectedOrder.tracking === 'delivered' ? 'Marked as Delivered' : 'Marked as Shipped'}
                    </span>
                    <p className="text-xs text-gray-500 mt-1 uppercase">Track ID: {selectedOrder.track_id}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
